import React, { FormEvent, useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Button, CenteredLoader, Chip, useToast } from '@app/core';
import {
  CriterionStatus,
  CriterionStatusEnum,
  ExtendedCriterion,
  NewQuota,
  Participation,
  ParticipationUpdateData,
  useAcceptParticipation,
  useApplicationAnswerStatus,
  useQuotas,
  useRecruitment,
  useUpdateQuotas,
} from '@app/api';
import { useApplicationExtendedCriterion } from './../../hooks/useApplicationExtendedCriterion';
import { useEnrichedQuotas } from './../../hooks/useRecruitmentQuotas';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useUpdateCriteria } from 'queries';
import { MutationOptions } from 'react-query';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.grayBackground,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    borderRadius: theme.borderRadius.std,
    paddingTop: theme.marginBase,
    paddingBottom: theme.marginBase,
  },
  title: {
    ...theme.fonts.body,
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase,
    marginLeft: 2 * theme.marginBase,
    marginRight: 2 * theme.marginBase,
  },
  criterionContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.white,
    width: '94%',
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase,
    borderRadius: theme.borderRadius.std,
  },
  buttons: {
    display: 'flex',
    alignSelf: 'flex-end',
    gap: 24,
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase,
    marginRight: 2 * theme.marginBase,
  },
  question: {
    ...theme.fonts.label,
    fontWeight: 'bold',
    margin: theme.marginBase,
    overflow: 'auto',
  },
  answerAndQuota: {
    display: 'flex',
    margin: theme.marginBase,
    justifyContent: 'space-between',
  },
  quotaLine: {
    display: 'flex',
    gap: 5 * theme.marginBase,
  },
  icon: {
    color: theme.colors.blue,
  },
  fontWeight: {
    fontWeight: 'bold',
  },
}));

interface Props {
  withTitle?: boolean;
  participation?: Participation;
  recruitmentId?: string;
  criteria?: ExtendedCriterion[];
  onAccept: () => any;
  onCancel: () => any;
  mutationOptions?: MutationOptions<
    Participation,
    Error,
    Participation,
    Participation | ParticipationUpdateData
  >;
}

export const ParticipationQuotaUpdate = ({
  withTitle = true,
  participation,
  recruitmentId,
  onAccept,
  onCancel,
  mutationOptions,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();

  const { data: recruitment } = useRecruitment(recruitmentId);
  const { data: formAnswerStatus, isLoading } = useApplicationAnswerStatus(
    participation?.mainApplicationId,
  );
  const { data: quotas } = useQuotas(recruitment?.sessionId, null, true);
  const { mutateAsync: updateCriteria, isLoading: isUpdating } =
    useUpdateCriteria(recruitmentId);
  const { mutateAsync: updateQuotas, isLoading: isUpdatingQuotas } =
    useUpdateQuotas(recruitment?.sessionId);
  const { mutateAsync: accept } = useAcceptParticipation(mutationOptions);
  const outOfQuotaStatuses = useMemo(() => {
    return formAnswerStatus?.filter(
      (s: CriterionStatus) => s.status === CriterionStatusEnum.OUT_OF_QUOTA,
    );
  }, [formAnswerStatus]);

  const outOfQuotaCriteria = useApplicationExtendedCriterion(
    recruitmentId,
    outOfQuotaStatuses?.map((s) => s.fieldRef),
  );

  const fullQuotas = quotas?.filter((q) =>
    outOfQuotaStatuses?.find((s) => {
      return s.item?.split(', ').includes(q.item.toString());
    }),
  );
  const fullQuotasWithLabel = useEnrichedQuotas(recruitmentId, fullQuotas);

  const applicationOutOfQuotaCriteria = useMemo(() => {
    type MergedQuota = NewQuota & { value?: number };
    const grouped = new Map<
      string,
      {
        criterionId?: string;
        fieldRef?: string;
        criterionLabel?: string;
        color?: string;
        quotas: MergedQuota[];
      }
    >();

    outOfQuotaStatuses?.forEach((s: CriterionStatus) => {
      const criterion = outOfQuotaCriteria.find(
        (e: ExtendedCriterion) => s.fieldRef === e.fieldRef,
      );

      const items = s.item?.split(', ') || [];
      const matchingQuotas: MergedQuota[] = items
        .map((itemStr) => {
          const oldQuota = criterion?.quotas?.find((q) => {
            if (typeof q.item === 'string') {
              return q.item === itemStr;
            } else if (typeof q.item === 'object' && 'min' in q.item) {
              return `${q.item.min}-${q.item.max ?? Infinity}` === itemStr;
            }
            return false;
          });

          const newQuota = fullQuotasWithLabel?.find((q) => {
            return q.item.toString() === itemStr;
          });

          return {
            ...oldQuota,
            ...newQuota,
            value: newQuota?.max ?? oldQuota?.value,
          } as MergedQuota;
        })
        .filter((q) => q.value !== undefined);

      const key = s.fieldRef || '';
      if (!grouped.has(key)) {
        grouped.set(key, {
          criterionId: criterion?.id,
          fieldRef: criterion?.fieldRef,
          criterionLabel: criterion?.fieldLabel,
          color: criterion?.color,
          quotas: [],
        });
      }
      grouped.get(key)!.quotas.push(...matchingQuotas);
    });

    return Array.from(grouped.values());
  }, [outOfQuotaStatuses, outOfQuotaCriteria, fullQuotasWithLabel]);

  const quotasToUpdate = useMemo(() => {
    if (!quotas || !fullQuotasWithLabel || fullQuotasWithLabel.length === 0) {
      return undefined;
    }

    const itemsToIncrement = new Set(
      fullQuotasWithLabel.map((q) => q.item.toString()),
    );

    return quotas.map((quota) => {
      const shouldIncrement = itemsToIncrement.has(quota.item.toString());

      return {
        item: quota.item,
        ref: quota.ref,
        value: shouldIncrement ? (quota.confirmed ?? 0) + 1 : quota.max ?? 0,
      };
    });
  }, [quotas, fullQuotasWithLabel]);

  const criteria = useMemo(() => {
    const baseCriteria = recruitment?.criteria ?? [];

    return baseCriteria.map((criterion) => {
      const matchedApplicationCriterion = applicationOutOfQuotaCriteria?.find(
        (a) => a.fieldRef === criterion.fieldRef,
      );

      if (!matchedApplicationCriterion) return criterion;

      return {
        ...criterion,
        quotas: criterion.quotas?.map((quota) => {
          const matchedQuota = matchedApplicationCriterion.quotas?.find(
            (mq) => mq.item === quota.item,
          );

          if (!matchedQuota) return quota;

          return {
            ...quota,
            value: (matchedQuota.confirmed ?? 0) + 1,
          };
        }),
      };
    });
  }, [recruitment, applicationOutOfQuotaCriteria]);

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      try {
        if (quotasToUpdate && quotasToUpdate.length > 0) {
          await updateQuotas(quotasToUpdate);
        }

        await updateCriteria(criteria);

        if (participation) {
          await accept(participation);
        }

        toast.saved();
        onAccept();
      } catch (e) {
        toast.exception(e);
      }
    },
    [
      updateCriteria,
      criteria,
      updateQuotas,
      quotasToUpdate,
      participation,
      accept,
      toast,
      onAccept,
    ],
  );

  if (isLoading || isUpdating || isUpdatingQuotas) {
    return <CenteredLoader />;
  }

  return (
    <div className={classes.container}>
      {withTitle && (
        <div className={classes.title}>
          <Trans
            i18nKey="sessions.details.monitoring.quota.increase"
            components={{
              b: <b />,
            }}
          />
        </div>
      )}
      {applicationOutOfQuotaCriteria?.map((qa) => (
        <div className={classes.criterionContainer} key={qa.fieldRef}>
          <div className={classes.question}>{qa.criterionLabel}</div>
          {qa.quotas?.map((quota, index) => (
            <div className={classes.answerAndQuota} key={index}>
              <Chip customColor={qa.color}>{quota?.label}</Chip>
              <div className={classes.quotaLine}>
                <div>
                  {quota?.confirmed}/<b>{quota?.value}</b>
                </div>
                <i
                  className={classnames('ri-arrow-right-line', classes.icon)}
                />
                <div>
                  {quota?.confirmed}/<b>{(quota?.confirmed ?? 0) + 1}</b>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
      <div className={classes.buttons}>
        <Button
          onClick={onCancel}
          color="red"
          type="button"
          variant="transparent"
          small
          text={t('sessions.details.monitoring.quota.cancelIncrease')}
        />
        <Button
          onClick={onSubmit}
          type="button"
          small
          text={t('sessions.details.monitoring.quota.acceptIncrease')}
        />
      </div>
    </div>
  );
};
